import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { TextArea } from '@shared/components/ui/shadcn/textarea';
import { UserCard } from '@shared/components/ui/usercard';
import { faListTimeline, faCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrderActivities } from '../../hooks/useOrderActivities';
import type { OrderActivity } from '../../models/Activities';

// Interface para el formato de historial (mantenida para compatibilidad futura)
// interface HistoryEntry {
//   id: number;
//   type: 'comment' | 'event';
//   date: string;
//   time: string;
//   title: string;
//   description?: string;
//   user: string;
// }

export const HistoryModal = forwardRef<ModalCenterRef>((_, ref) => {
  const { t } = useTranslation('viewOrdersModule');
  const [comment, setComment] = useState('');
  const { data: activities, isLoading, error } = useOrderActivities();

  const handleAddComment = () => {
    if (comment.trim()) {
      console.log('Adding comment:', comment);
      setComment('');
    }
  };

  // Función para formatear la fecha de la actividad
  const formatActivityDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Función para formatear la hora de la actividad
  const formatActivityTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Función para formatear la descripción de la actividad
  const formatActivityDescription = (activity: OrderActivity) => {
    if (!activity.description?.content) return '';

    return activity.description.content
      .map((item) => {
        const field = item.new.field || item.old.field;
        const newValue = item.new.value;

        if (field && newValue !== null) {
          return `${field}: ${newValue}`;
        } else if (newValue !== null) {
          return String(newValue);
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  };

  // Convertir actividades a formato de historial agrupadas por fecha
  const historyData = activities
    ? Object.entries(activities)
        .map(([dateKey, activitiesForDate]) => ({
          date: formatActivityDate(activitiesForDate[0]?.date || dateKey),
          activities: activitiesForDate
            .map((activity, index) => ({
              id: `${dateKey}-${index}`,
              type: 'event' as const,
              time: formatActivityTime(activity.date),
              title: activity.title,
              description: formatActivityDescription(activity),
              user: activity.user,
            }))
            .sort((a, b) => {
              // Ordenar por hora dentro del mismo día (más reciente primero)
              const timeA = new Date(`2000-01-01 ${a.time}`);
              const timeB = new Date(`2000-01-01 ${b.time}`);
              return timeB.getTime() - timeA.getTime();
            }),
        }))
        .sort((a, b) => {
          // Ordenar por fecha (más reciente primero)
          const dateA = new Date(a.date.split('/').reverse().join('-'));
          const dateB = new Date(b.date.split('/').reverse().join('-'));
          return dateB.getTime() - dateA.getTime();
        })
    : [];

  return (
    <ModalCenter ref={ref} title={t('modals.history.title')} width="xl" type="default" fullScreenMobile={true}>
      <div className="py-6 px-2 max-h-[85vh] overflow-y-auto">
        {/* Sección para añadir comentario
        <div className="mb-6 relative">
          <TextArea
            placeholder={t('modals.history.comment-placeholder')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="resize-none h-20"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={handleAddComment}
            disabled={!comment.trim()}
            className="absolute right-2 bottom-2 bg-lines"
          >
            {t('modals.history.comment-button')}
          </Button>
        </div> */}

        {/* Lista de histórico */}
        <div className="space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-sm text-complementary-text">{t('modals.history.loading')}</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-sm text-subtext">{t('modals.history.error')}</span>
            </div>
          ) : historyData.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-sm text-subtext">{t('modals.history.empty')}</span>
            </div>
          ) : (
            historyData.map((dateGroup) => (
              <>
                <div key={dateGroup.date}>
                  {/* Encabezado de fecha */}
                  <div className="flex items-center gap-2 mb-4">
                    <FontAwesomeIcon icon={faListTimeline} className="!w-3 !h-3 text-text" />
                    <span className="text-sm font-medium text-text">{dateGroup.date}</span>
                  </div>

                  {/* Actividades del día */}
                  <div className="relative">
                    {/* Línea vertical que conecta todas las actividades */}
                    <div className="absolute left-2 top-1 bottom-0 w-px bg-lines"></div>

                    <div className="space-y-4">
                      {dateGroup.activities.map((activity, activityIndex) => (
                        <>
                          <div
                            key={activity.id}
                            className={`flex items-start gap-4 relative !mt-0 ${
                              activityIndex === dateGroup.activities.length - 1 ? 'bg-body-bg' : 'bg-transparent'
                            }`}
                          >
                            {/* Círculo de la actividad */}
                            <div className="relative z-10 flex items-start border-[5px] border-body-bg rounded-full ml-[-2px]">
                              <FontAwesomeIcon icon={faCircle} className="!w-3 !h-3 text-subtext bg-body-bg" />
                            </div>

                            {/* Contenido de la actividad */}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm">
                                {activity.title} -{' '}
                                <span className="text-complementary-text text-xs">{activity.time}</span>
                              </h4>

                              <div>
                                <div className="sm:flex sm:items-end sm:justify-between">
                                  {activity.description && (
                                    <p className="text-xs mt-1 whitespace-pre-line leading-loose">
                                      {activity.description}
                                    </p>
                                  )}
                                  <UserCard name={activity.user} email="" />
                                </div>
                                {/* Línea divisoria solo si no es la última actividad */}
                                {activityIndex < dateGroup.activities.length - 1 && (
                                  <div className="my-4 border-t border-card-border"></div>
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            ))
          )}
        </div>
      </div>
    </ModalCenter>
  );
});

HistoryModal.displayName = 'HistoryModal';
